import React, { useContext } from 'react';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useSelector } from 'react-redux';
import { voSelectors } from 'redux/voReducer';
import { Button, Stack } from '@mui/material';
import { DropzoneArea } from 'mui-file-dropzone';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CustomDialog } from 'components/shared/Dialog';
import { DocumentContext } from 'contexts/DocumentContext';
import { AutocompleteField, InputField, SelectField } from 'components/form';
import { UploadedFileView } from 'components/shared/UploadedFileView/UploadedFileView';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { useInvoice } from '../../../hooks';
import { UploadInvoiceFormValues, uploadInvoiceSchema } from '../../../model';

export const UploadInvoiceDialog = () => {
  const documentContext = useContext(DocumentContext);

  const { isUploading, handleUploadInvoice } = useInvoice();

  const { isOpen, data, handleClose } = useModal(ModalsKeys.UploadInvoiceDialog);

  const dealerContactsToNotifyOptions = useSelector(voSelectors.selectDealerContactsToNotify);
  const lenderContactsToNotifyOptions = useSelector(voSelectors.selectLenderContactsToNotify);

  const { control, setValue, handleSubmit } = useForm<UploadInvoiceFormValues>({
    resolver: zodResolver(uploadInvoiceSchema),
    defaultValues: { type: 'invoice', file: null, description: '', dealerContact: [], lenderContact: [] },
  });

  const file = useWatch({ control, name: 'file' });

  const handleDropFile = (files: File[]) => {
    setValue('file', files[0]);
  };

  const handleSubmitForm = async (values: UploadInvoiceFormValues) => {
    if (!values.file) return;

    const dto = {
      docType: values.type,
      vendorOpportunityId: data.VOId,
      docName: values.file?.name ?? '',
      invoiceQuoteNumber: values.description,
      dynamicsVendorOpportunityId: data.dynamicsVOId,
      docDescription: `${values.type}#${values.description}`,
      notifyUserProfileIds: [...values.dealerContact, ...values.lenderContact].map(({ value }) => value),
    };

    const response = await handleUploadInvoice(values.file, dto, api => ({
      refetchQueries: [api.getInvoiceDocument],
      awaitRefetchQueries: true,
    }));

    documentContext.setLastDocument(response);

    handleClose();
  };

  return (
    <CustomDialog
      isOpen={isOpen}
      maxWidth="md"
      onClose={handleClose}
      label="Upload Invoice/Quote"
      actions={
        <>
          <Button size="small" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>

          <Button
            size="small"
            type="submit"
            disabled={!file}
            variant="contained"
            loading={isUploading}
            onClick={handleSubmit(handleSubmitForm)}
          >
            Confirm
          </Button>
        </>
      }
    >
      <Stack gap="1rem">
        <Stack direction="row" alignItems="center" gap="1rem">
          <AutocompleteField
            multiple
            size="small"
            limitTags={3}
            control={control}
            name="dealerContact"
            options={dealerContactsToNotifyOptions}
            textFieldProps={{
              size: 'small',
              variant: 'outlined',
              label: 'Dealer Contact to Notify',
            }}
          />

          <AutocompleteField
            multiple
            size="small"
            limitTags={3}
            control={control}
            name="lenderContact"
            options={lenderContactsToNotifyOptions}
            textFieldProps={{
              size: 'small',
              variant: 'outlined',
              label: 'Lender Contact to Notify',
            }}
          />
        </Stack>

        <RenderWithCondition condition={!file}>
          <DropzoneArea
            filesLimit={1}
            fileObjects={[]}
            showPreviews={false}
            maxFileSize={25000000}
            onDrop={handleDropFile}
            showFileNamesInPreview={false}
            dropzoneText="Drag or Click Here To Upload File"
          />
        </RenderWithCondition>

        {!!file && (
          <>
            <UploadedFileView size={file.size} name={file.name} onDelete={() => setValue('file', null)} />

            <Stack direction="row" alignItems="center" gap="1rem">
              <SelectField
                required
                label="Type"
                size="small"
                name="type"
                control={control}
                variant="outlined"
                options={[
                  { label: 'Quote', value: 'quote' },
                  { label: 'Invoice', value: 'invoice' },
                ]}
              />

              <InputField
                name="description"
                control={control}
                label="Invoice/Quote Number"
                inputProps={{
                  required: true,
                  variant: 'outlined',
                }}
              />
            </Stack>
          </>
        )}
      </Stack>
    </CustomDialog>
  );
};
